import { searchClient } from "@app/api2/Clients";
import { useState } from "react";
import { Button, Dropdown, Grid, GridColumn, GridRow, Icon, Select, TextArea } from "@/components/bp/bp-ui";
import { PersonSearchValidationSchema, PersonValidationSchema } from "../../../Manage/EnrollmentForm.Schema";
import { CustomValueEditor } from "./ValueEditors";
import { useQuery } from "@tanstack/react-query";
import { BPReactTable } from "@app/components/React/Common/BPReactTable";
import { CustomAddGroupAction, CustomAddRuleAction, CustomCombinatorSelector, CustomNotToggle, CustomRemoveGroupAction, CustomRemoveRuleAction } from "./Actions";
import { getFieldsFromSchemaFlattenArrays } from "./Meta";
import { QueryBuilderDnD } from '@react-querybuilder/dnd';
import * as ReactDnD from 'react-dnd';
import * as ReactDndHtml5Backend from 'react-dnd-html5-backend';
import * as ReactDndTouchBackend from 'react-dnd-touch-backend';
import QueryBuilder, { FieldSelectorProps, MatchModeEditor, MatchModeEditorProps, Rule, RuleGroupType, RuleProps, ValueSelectorProps, defaultValidator, formatQuery } from "react-querybuilder";
import { useTranslation2 } from "@app/components/React/Contexts/TranslationContext2";
import { BPButton } from "@/components/bp/bpbutton";


type EnrollmentSearchQueryBuilderProps = {
    flattenArrays?: boolean;
    query?: RuleGroupType;
    onQueryChange?: (query: RuleGroupType) => void;
}

function queryToObject(query: RuleGroupType): Record<string, any> {
    let obj = {};
    query.rules.forEach(rule => {
        if ('combinator' in rule) {
            obj[query.id] = queryToObject(rule);
        } else {
            if (rule.match?.mode)
                obj[rule.field] = queryToObject(rule.value);
            else
                obj[rule.field] = rule.value;
        }
    });
    return obj;
}

export const EnrollmentSearchQueryBuilder: React.FC<EnrollmentSearchQueryBuilderProps> = ({ flattenArrays, query: queryIn, onQueryChange }) => {
    const { translations: t } = useTranslation2();
    const [query, setQuery] = useState<RuleGroupType>(queryIn ?? { combinator: 'and', rules: [] });

    const personsQuery = useQuery({
        queryKey: ['enrollment-search-persons', query],
        queryFn: async () => {

            // const queryObj = queryToObject(query);
            // console.log('queryObj:', queryObj);
            const response = await searchClient.searchByQueryBuilder({ query: JSON.parse(formatQuery(query, 'json')) });
            return { debug: { sql: response.sql, linq: response.linq }, data: response.items, totalRows: response.items.length };
        },
        gcTime: 0,
        // cacheTime: 0,
        enabled: false,
    });

    // let bioFields = getFieldsFromSchema(BiographicalSchema.fields);
    // let physicalFields = getFieldsFromSchema(PhysicalSchema.fields);
    // let contactFields = getFieldsFromSchema(ContactSchema.fields);
    // let addressFields = getFieldsFromSchema(AddressSchema.fields);
    // let enrollmentFields = getFieldsFromSchema(PersonSearchValidationSchema.fields);
    let schemaFields = !flattenArrays ? PersonSearchValidationSchema(t).fields : PersonValidationSchema(t).fields;
    let enrollmentFields = getFieldsFromSchemaFlattenArrays(schemaFields, '', flattenArrays ?? false);

    // const fields = bioFields.concat(physicalFields).concat(contactFields).concat(addressFields).concat(enrollmentFields);
    const fields = enrollmentFields

    // const fields = [
    //     { label: 'First', options: [{ value: 'firstName', label: 'First Name' }] },
    //     { label: 'Last', options: [{ value: 'lastName', label: 'Last Name' }] },
    // ]
    // const fields = enrollmentFields

    const scrollablePreStyle: React.CSSProperties = {
        // maxHeight: '400px',
        overflowY: 'auto',
        whiteSpace: 'pre',
        // overflowWrap: 'normal',
    };

    return (<>
        <QueryBuilderDnD dnd={{ ...ReactDnD, ...ReactDndHtml5Backend, ...ReactDndTouchBackend }}>
            <QueryBuilder
                validator={defaultValidator}
                autoSelectField
                showNotToggle={true}
                fields={fields}
                query={query}
                getOperators={(field => (['=', "!="]))}
                getDefaultOperator={(field => '=')}
                getSubQueryBuilderProps={field => ({
                    getOperators: (subField => (['=', "!="])),
                    getDefaultOperator: (subField => '='),
                    // Customize the sub-query builder props based on the field
                    // For example, you can set the available fields for the sub-query
                    // fields: enrollmentFields.filter(f => f.id !== field.id)
                })}
                onQueryChange={(query) => {
                    setQuery(query);
                    onQueryChange?.(query);
                }}
                controlElements={{
                    valueEditor: CustomValueEditor,
                    valueSelector: CustomValueSelector,
                    fieldSelector: CustomFieldSelector,
                    combinatorSelector: CustomCombinatorSelector,
                    addRuleAction: CustomAddRuleAction,
                    removeRuleAction: CustomRemoveRuleAction,
                    addGroupAction: CustomAddGroupAction,
                    removeGroupAction: CustomRemoveGroupAction,
                    matchModeEditor: CustomMatchModeEditor,
                    rule: CustomRuleLayout,
                    notToggle: CustomNotToggle,


                }}
                showCombinatorsBetweenRules={false}
                showShiftActions={false}
                controlClassnames={{
                    // actionElement: 'ui button',
                    // valueSelector: 'ui dropdown',
                    // operators: 'ui dropdown',
                    // value: 'ui input',
                    queryBuilder: 'queryBuilder-branches queryBuilder-justified'
                }}
            />
        </QueryBuilderDnD>
        <BPButton type='button' onClick={async (ev) => {
            ev.preventDefault();
            await personsQuery.refetch();
            // return console.log('Current Query:', query);
        }}><Icon name='search' /></BPButton>
        <BPReactTable
            data={personsQuery?.data?.data ?? []}
            // dataSource={async ()=>{
            //     let response = await searchClient.searchByQueryBuilder({ query: JSON.parse(formatQuery(query, 'json')) })
            //     return { data: response.items, totalRows: response.items.length  };
            // }}
            columns={[
                { id: 'id', Header: 'ID', accessor: 'id' },
                { id: 'firstName', Header: 'First Name', accessor: 'firstName' },
                { id: 'lastName', Header: 'Last Name', accessor: 'lastName' },
                { id: 'dateOfBirth', Header: 'Date of Birth', accessor: 'dob' },
            ]}
        />
        <Grid>
            <GridRow columns={2}>
                <GridColumn>
                    <pre style={scrollablePreStyle}>{formatQuery(query, 'sql')}</pre>
                    <pre style={scrollablePreStyle}>{formatQuery(query, 'natural_language')}</pre>
                    <pre style={scrollablePreStyle}>{personsQuery.data?.debug?.sql}</pre>
                    <pre style={scrollablePreStyle}>{personsQuery.data?.debug?.linq}</pre>
                </GridColumn>
                <GridColumn>
                    <pre style={scrollablePreStyle}>{formatQuery(query, 'json')}</pre>
                </GridColumn>
            </GridRow>
        </Grid>
    </>

    );
};

const dateFormat = 'yyyy-MM-dd';



function CustomMatchModeEditor(props: MatchModeEditorProps) {
    return null;
    return <MatchModeEditor {...props} />;
}

function CustomValueSelector(props: ValueSelectorProps) {
    const { value, handleOnChange, options } = props;
    return <Select value={value} search onChange={(e, { value }) => handleOnChange(value)} options={options.map((option) => ({ key: option.value, value: option.value, text: option.label }))} />;
    // return <ValueSelector {...props} />;
}

function CustomFieldSelector(props: FieldSelectorProps) {
    // console.log('CustomFieldSelector props:', props);
    const { value, handleOnChange, options } = props;
    // return <ValueSelector {...props} />;
    return <Dropdown selection style={{ minWidth: 300 }} value={value} search
        onChange={(e, { value }) => handleOnChange(value)}
        options={options.map((option) => ({
            key: option.value,
            value: option.value,
            text: option.label,
            options: option.options,
            description: option.description
        }))} />;

}

export function CustomRuleLayout(props: RuleProps) {
    // console.log('CustomRuleLayout props:', props);
    return <Rule {...props} />
}




// function getFieldsFromSchema(schemaFields) {
//     // console.log('schemaFields:', schemaFields);
//     const showAsSubproperties = true
//     const fields = Object.keys(schemaFields).map(key => {
//         let field = schemaFields[key];
//         return {
//             name: key,
//             label: field?.spec.label ?? key,
//             datatype: field?.type,
//             inputType: field?.type,
//             matchModes: showAsSubproperties && (field?.type === 'array' || field?.type === 'object') ? true : false,
//             subproperties: showAsSubproperties && ((field?.type === 'array') || (field?.type === 'object')) ? getFieldsFromSchema(field?.innerType?.fields ?? field?.fields ?? {}) : undefined,
//             options: !showAsSubproperties && ((field?.type === 'array') || (field?.type === 'object')) ? getFieldsFromSchema(field?.innerType?.fields ?? field?.fields ?? {}) : undefined,
//         };
//     });
//     return fields;
// }


